import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus, Coins } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFinancialWidgets } from '@/hooks/use-financial-widgets';
import { Skeleton } from '@/components/ui/skeleton';

interface TotalAssetValueCardProps {
  className?: string;
}

export function TotalAssetValueCard({ className }: TotalAssetValueCardProps): React.ReactElement {
  const { useTotalAssetValue } = useFinancialWidgets();
  const { data, isLoading, error } = useTotalAssetValue();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-GB').format(value);
  };

  if (isLoading) {
    return (
      <Card className={cn('relative overflow-hidden shadow-sm bg-white dark:bg-black', className)}>
        <CardHeader>
          <CardTitle className="text-base font-semibold tracking-tight">Total Asset Value</CardTitle>
          <CardDescription>Current total value of all assets</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-6 w-1/2" />
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className={cn('relative overflow-hidden shadow-sm bg-white dark:bg-black', className)}>
        <CardHeader>
          <CardTitle className="text-base font-semibold tracking-tight">Total Asset Value</CardTitle>
          <CardDescription>Current total value of all assets</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">Failed to load asset value data.</div>
        </CardContent>
      </Card>
    );
  }

  const getTrendIcon = () => {
    switch (data.trendDirection) {
      case 'up':
        return <TrendingUp className="h-5 w-5 text-green-600" />;
      case 'down':
        return <TrendingDown className="h-5 w-5 text-red-600" />;
      default:
        return <Minus className="h-5 w-5 text-gray-600" />;
    }
  };

  const getTrendColor = () => {
    switch (data.trendDirection) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getTrendPrefix = (direction: string) => {
    switch (direction) {
      case 'up':
        return '+';
      case 'down':
        return '-';
      default:
        return '';
    }
  };

  return (
    <Card className={cn('relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black', className)}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Total Asset Value</CardTitle>
            <CardDescription>Current total value of all assets</CardDescription>
          </div>
          <div className="p-2 bg-blue-50 dark:bg-blue-950 rounded-lg">
            <Coins className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Main Value */}
        <div className="space-y-1">
          <div className="text-3xl font-bold tracking-tight">
            {formatCurrency(data.totalValue)}
          </div>
          <div className="text-sm text-muted-foreground">
            {formatNumber(data.totalAssets)} {data.totalAssets === 1 ? 'asset' : 'assets'}
          </div>
        </div>

        {/* Trend Indicator */}
        <div className="flex items-center gap-2 pt-2 border-t">
          <div className="flex items-center gap-1">
            {getTrendIcon()}
            <span className={cn('text-sm font-medium', getTrendColor())}>
              {Math.abs(data.changePercentage).toFixed(1)}%
            </span>
          </div>
          <span className="text-sm text-muted-foreground">
            {getTrendPrefix(data.trendDirection)}
            {formatCurrency(Math.abs(data.changeAmount))} from last period
          </span>
        </div>

        {/* Average Value */}
        <div className="pt-2 border-t">
          <div className="text-xs text-muted-foreground">Average Asset Value</div>
          <div className="text-lg font-semibold">
            {formatCurrency(data.totalAssets > 0 ? data.totalValue / data.totalAssets : 0)}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
